import React, { useEffect, useState } from "react";
import Image from "@components/AppImage";
import Icon from "@components/AppIcon";

const FounderJourney = () => {
  const [founders, setFounders] = useState([]);
  const [journeySteps, setJourneySteps] = useState([]);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const res = await fetch("/api/about_us");
        const data = await res.json();

        if (Array.isArray(data) && data.length > 0) {
          const aboutEntry = data[0];
          const sections = aboutEntry.sections || [];

          // Founders section (must be enabled)
          const founderSection = sections.find(
            (s) => s.type === "our_founders" && s.enabled
          );
          if (founderSection && founderSection.our_founders) {
            const mappedFounders = founderSection.our_founders
              .filter((f) => f.enabled) // only enabled founders
              .map((f) => ({
                name: f.doctors_name,
                credentials: f.qualifications,
                description: f.description,
                image: f.image ? `/storage/${f.image}` : null,
              }));
            setFounders(mappedFounders);
          }

          // Journey section (must be enabled)
          const journeySection = sections.find(
            (s) => s.type === "our_journey" && s.enabled
          );
          if (journeySection && journeySection.our_journey) {
            const mappedJourney = journeySection.our_journey
              .filter((j) => j.enabled)
              .map((j) => ({
                year: j.year,
                title: j.title,
                description: j.journey_description?.[0]?.content?.[0]?.text || "",
              }));
            setJourneySteps(mappedJourney);
          }
        }
      } catch (error) {
        console.error("Error fetching About Us (founders/journey):", error);
      }
    };

    fetchAbout();
  }, []);

  if (founders.length === 0 && journeySteps.length === 0) {
    return null; // hide section entirely if nothing enabled
  }

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        {/* Founders Grid */}
        {founders.length > 0 && (
          <div className="mb-16">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-[#08005f] text-center mb-10">
              Meet Our Founders
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {founders.map((f, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-6 text-center medical-shadow border border-[#08005f]/10"
                >
                  <div className="w-32 h-32 mx-auto mb-4 rounded-2xl overflow-hidden">
                    <Image
                      src={f.image}
                      alt={f.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-[#08005f]">
                    {f.name}
                  </h3>
                  <p className="text-sm text-text-secondary mb-2">
                    {f.credentials}
                  </p>
                  <p className="text-sm text-text-secondary italic">
                    {f.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Journey Timeline */}
        {journeySteps.length > 0 && (
          <div>
            <h3 className="font-heading font-bold text-2xl text-[#08005f] text-center mb-8">
              Our Journey Timeline
            </h3>
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#08005f]/20"></div>
              <div className="space-y-8">
                {journeySteps.map((step, index) => (
                  <div
                    key={index}
                    className="relative flex items-start space-x-6"
                  >
                    <div className="w-12 h-12 bg-[#08005f] rounded-full flex items-center justify-center">
                      <Icon name="Flag" size={20} color="white" />
                    </div>
                    <div className="flex-1 bg-white rounded-2xl p-6 medical-shadow border border-[#08005f]/10">
                      <span className="bg-[#08005f]/10 text-[#08005f] px-3 py-1 rounded-full text-sm font-medium">
                        {step.year}
                      </span>
                      <h4 className="font-heading font-semibold text-lg text-[#08005f] mt-2">
                        {step.title}
                      </h4>
                      <p className="text-text-secondary text-sm">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FounderJourney;
